import type { ChakraStyledOptions } from "@chakra-ui/react";
import { Flex } from "@chakra-ui/react";
import SCImage from "commons/sc/SCImage";
import VideoPlayer from "commons/ui/VideoPlayer";
import BundleIcon from "commons/ui/BundleIcon";
import SCLinkButton from "commons/sc/SCLinkButton";
import SCText from "commons/sc/SCText";
import SCHeading from "commons/sc/SCHeading";
import SCRichText from "commons/sc/SCRichText";
import type { PartnerResult } from "components/PartnersFilter";

interface LinksProps extends ChakraStyledOptions {
    partner: PartnerResult;
}

const Links = ({ partner, ...props }: LinksProps) => {
    const website = partner.websiteLink;
    const linkedin = partner.linkedInLink;
    const downloadLink = partner.downloadLink;
    return (
        <Flex gap={["sp24", null, "sp42"]} marginTop={["sp24", null, "0"]} {...props}>
            {website && (
                <SCLinkButton
                    linkField={website.jsonValue}
                    display="flex"
                    flexDirection={["column", null, "row"]}
                    variant="unstyledWithHover"
                    size="md"
                    leftIcon={<BundleIcon name="Globe" width="24px" height="24px" />}
                />
            )}
            {linkedin && (
                <SCLinkButton
                    linkField={linkedin.jsonValue}
                    display="flex"
                    flexDirection={["column", null, "row"]}
                    variant="unstyledWithHover"
                    size="md"
                    leftIcon={<BundleIcon name="Linkedin" width="24px" height="24px" />}
                />
            )}
            {downloadLink && (
                <SCLinkButton
                    linkField={downloadLink.jsonValue}
                    display="flex"
                    flexDirection={["column", null, "row"]}
                    variant="unstyledWithHover"
                    size="md"
                    leftIcon={<BundleIcon name="Download" width="24px" height="24px" />}
                />
            )}
        </Flex>
    );
};

const PartnerLayout = (partner: PartnerResult) => {
    const logo = partner.logo;
    const videoUrl = partner.video?.value;

    const thumbnail = {
        thumbnailAlt: partner.partnerName?.value,
        thumbnailImage: partner.thumbnailImage?.jsonValue,
        thumbnailUrl: partner.thumbnailUrl?.value,
    };

    return (
        <Flex
            gap={["sp32", null, "sp80"]}
            flexDirection={["column-reverse", null, "row"]}
            py={"sp40"}
        >
            <Flex flexDirection="column" width={["100%", null, "50%"]} gap="sp24">
                <SCImage
                    imageField={logo.jsonValue}
                    width={parseInt(logo.jsonValue.value?.width as string) ?? 200}
                    height={parseInt(logo.jsonValue.value?.height as string) ?? 80}
                    display={["none", null, "block"]}
                    mb={["sp16", null, "0"]}
                    w="50%"
                    h="auto"
                    sx={{
                        objectFit: "contain",
                    }}
                />

                {videoUrl && <VideoPlayer videoUrl={videoUrl} thumbnail={thumbnail} />}

                <Flex flexDirection="column" gap="sp12">
                    <SCHeading as="h3" size="h5" textField={partner?.teaserHeadline} />

                    <SCText size="body" textField={partner?.teaserText} />
                </Flex>

                <Links partner={partner} display={["flex", null, "none"]} />
            </Flex>

            <Flex flexDirection="column" width={["100%", null, "50%"]} gap={["sp16", null, "sp24"]}>
                <SCImage
                    imageField={logo.jsonValue}
                    width={parseInt(logo.jsonValue.value?.width as string) ?? 200}
                    height={parseInt(logo.jsonValue.value?.height as string) ?? 80}
                    display={["block", null, "none"]}
                    mb={["sp16", null, "0"]}
                    w="50%"
                    h="auto"
                    sx={{
                        objectFit: "contain",
                    }}
                />
                <SCHeading as="h2" size="h3" mb="0" textField={partner?.headline} />

                <SCText size="h5" textField={partner?.subHeadline} />

                <SCRichText richTextField={partner?.text} />

                <Links partner={partner} display={["none", null, "flex"]} />
            </Flex>
        </Flex>
    );
};

export default PartnerLayout;
